// Rows of the territory detail's store table (WEB_PLAN.md §5.8: name, days since visit, priority, value, stage, plus
// owner). Each row carries the sort keys of TerritoryDetailView.RecordSort, so header sorting gives the iOS order:
// days since visit puts never-visited stores first, then the stalest; priority and value go highest first; stage goes
// by stage order with "no stage" last.

import { PRIORITY_SCORES, type Priority } from '@/contracts/enums';
import { recordDisplayName, type RecordPayload, type StagePayload, type UserPayload } from '@/contracts/models';
import { statsOf } from '@/domain/derived';
import { stageFor } from '@/domain/pipelines';
import { daysSinceVisit, recordSubtitle, type RecordLookups } from '@/domain/records';
import { fullName, lookup } from '@/domain/util';

export interface TerritoryStoreRow {
  id: string;
  name: string;
  subtitle: string;
  lastVisitAt: Date | undefined;
  /** Calendar days since the last visit; never visited = MAX_SAFE_INTEGER (the stalest). */
  daysSort: number;
  priority: Priority;
  priorityScore: number;
  value: number;
  stage: StagePayload | undefined;
  /** Stage order; no stage = MAX_SAFE_INTEGER (last). */
  stageOrder: number;
  owner: UserPayload | undefined;
  /** "" when unassigned (sorts first ascending). */
  ownerName: string;
}

export const NEVER = Number.MAX_SAFE_INTEGER;

export function territoryStoreRows(stores: readonly RecordPayload[], lk: RecordLookups): TerritoryStoreRow[] {
  return stores.map((r) => {
    const s = statsOf(lk.stats, r.id);
    const days = daysSinceVisit(s, lk.now);
    const stage = stageFor(r, lk.pipelines);
    const owner = lookup(lk.users, r.ownerId);
    return {
      id: r.id,
      name: recordDisplayName(r),
      subtitle: recordSubtitle(r),
      lastVisitAt: s.lastVisitAt,
      daysSort: days === undefined ? NEVER : days,
      priority: r.priority,
      priorityScore: PRIORITY_SCORES[r.priority] ?? 0,
      value: Number.isFinite(r.value) ? r.value : 0,
      stage,
      stageOrder: stage ? stage.order : NEVER,
      owner,
      ownerName: owner ? fullName(owner) : '',
    };
  });
}
